import { forwardRef, useState } from "react";
import { Input, InputProps } from "./input";

type NumericInputProps = Omit<InputProps, "type"> & {
  onValueChange?(value: number): void;
  maxIntegerDigits?: number;
};

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  ({ onValueChange, value, onChange, maxIntegerDigits = 10, ...props }, ref) => {
    const [formattedValue, setFormattedValue] = useState<string>(value ? value.toLocaleString("en-GB") : "");

    return (
      <Input
        ref={ref}
        value={formattedValue}
        onChange={(e) => {
          const element = e.target;
          let numericValue = element.value.replace(/[^0-9.]/g, "");

          // Prevent second decimal point being added
          if ((numericValue.match(/\./g) ?? []).length > 1) return;

          // Limit integer part to maxIntegerDigits
          if (maxIntegerDigits) {
            const decimalIndex = numericValue.indexOf(".");
            const integerPart = decimalIndex !== -1 ? numericValue.slice(0, decimalIndex) : numericValue;
            if (integerPart.length > maxIntegerDigits) return;
          }

          // Prevent more than 2 decimal places
          const decimalsToRemove = numericValue.split(".")[1]?.length - 2;
          if (decimalsToRemove > 0) {
            numericValue = numericValue.slice(0, -decimalsToRemove);
          }

          const actualValue = isNaN(Number(numericValue)) ? 0 : Number(numericValue);
          onValueChange?.(actualValue);

          // Add commas if the number is 1000 or more
          if (Number(numericValue) >= 1000) {
            const parts = numericValue.split(".");
            parts[0] = Number(parts[0]).toLocaleString("en-GB");
            numericValue = parts.join(".");
          }

          setFormattedValue(numericValue);

          // Adjust pointer position after formatting
          if (element.selectionStart === null) return;
          let pointer = Math.max(0, element.selectionStart + numericValue.length - element.value.length);
          if (!Number.isInteger(actualValue) && numericValue.length - pointer <= 2) {
            pointer++;
          }
          window.requestAnimationFrame(() => {
            element.selectionStart = pointer;
            element.selectionEnd = pointer;
          });

          // Call the original onChange if provided
          onChange?.(e);
        }}
        inputMode={"numeric"}
        type="text"
        {...props}
      />
    );
  },
);

NumericInput.displayName = "NumericInput";
